package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.withFrameNanos
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.Res
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.material_symbols_filled
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.material_symbols_outlined
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.public_sans
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.vazirmatn
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeSource
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.preloadFont

/**
 * Web: hold the first composition until all four app fonts are in Skia's font collection, then pump
 * frames for a few seconds so string/vector resources that stream in after the first frame actually
 * paint (their invalidations are otherwise coalesced into a frame that never gets scheduled while
 * the page sits idle on the first screen).
 *
 * KNOWN UPSTREAM ISSUE (2026-07-24, CMP 1.11.1, no stable fix available): `BaseComposeScene`
 * executes gesture callbacks (clickable's onClick) via an internal dispatcher that in practice only
 * drains when the NEXT pointer event arrives — a tap whose release is the last pointer event the
 * page sees (synthetic clicks; possibly real touch taps) stays pending until the next interaction.
 * Verified via DOM-event instrumentation; app-level workarounds tried and REJECTED (all
 * ineffective): post-release synthetic pointermove nudges (index.html shim), post-release
 * `withFrameNanos` pumps, post-release draw-invalidation pulses. Do not retry those. Re-test on the
 * next CMP upgrade and check upstream (JetBrains/compose-multiplatform) for a fix; details in
 * docs/PROGRESS.md § 2026-07-24 web tap investigation.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
actual fun AppResourceGate(content: @Composable () -> Unit) {
    val fonts = listOf(
        preloadFont(Res.font.public_sans),
        preloadFont(Res.font.vazirmatn),
        preloadFont(Res.font.material_symbols_outlined),
        preloadFont(Res.font.material_symbols_filled),
    )
    if (fonts.all { it.value != null }) {
        StartupFramePump()
        content()
    }
}

/**
 * Requests a frame a few times a second while the app is young so resources that finish loading
 * after the first frame get painted, then goes quiet — after that normal interaction-driven
 * invalidation has long taken over.
 *
 * MUST be a low-frequency heartbeat with `delay` between frames: a tight `withFrameNanos` loop on
 * the single JS thread starves the queued resource-load coroutines, delaying the very loads it is
 * meant to paint (measured: the strings .cvr fetch fired the moment a 10s full-rate phase ended).
 */
@Composable
private fun StartupFramePump() {
    LaunchedEffect(Unit) {
        val start = TimeSource.Monotonic.markNow()
        while (start.elapsedNow() < 60.seconds) {
            delay(250)
            withFrameNanos { }
        }
    }
}
